import React from 'react';
import { experience } from './data/experience';
import { techGroups } from './data/techGroups';
import useInView from '../hooks/useInView';

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

// "October 2024 - Present" -> "2024.10 - now". A bare year stays a bare year.
const formatPart = (part) => {
  const text = part.trim();
  if (/^present$/i.test(text)) return 'now';
  const match = /^([A-Za-z]+)\s+(\d{4})$/.exec(text);
  const month = match ? MONTHS.indexOf(match[1].toLowerCase()) : -1;
  if (match && month >= 0) return `${match[2]}.${String(month + 1).padStart(2, '0')}`;
  return text;
};

export const formatDate = (title) => title.split(' - ').map(formatPart).join(' - ');

const isCurrent = (title) => /present/i.test(title);

// Up to two initials from the company name, ignoring bracketed suffixes: "ESPC (UK)" -> "E".
const getInitials = (company) =>
  company
    .replace(/\(.*?\)/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

// Splits a flat tech list into the labelled rows from data/techGroups.js.
const groupTech = (tech) => {
  const rows = techGroups.map((group) => ({ ...group, tags: [] }));
  const fallback = rows[rows.length - 1];
  tech.forEach((tag) => {
    const row = rows.find((group) => group.items.includes(tag)) || fallback;
    row.tags.push(tag);
  });
  return rows.filter((row) => row.tags.length > 0);
};

const LogRow = ({ item }) => {
  const [ref, inView] = useInView();
  const current = isCurrent(item.title);

  return (
    <li
      ref={ref}
      className={`log-row reveal${inView ? ' in-view' : ''}${current ? ' is-current' : ''}`}
    >
      <p className="log-date">{formatDate(item.title)}</p>
      <article className="log-body">
        <div className="log-head">
          {item.logo ? (
            <img src={item.logo} alt={`${item.company} logo`} className="log-logo" />
          ) : (
            <span className="log-initials" aria-hidden="true">
              {getInitials(item.company)}
            </span>
          )}
          <h3>{item.company}</h3>
          <p className="log-role">{item.role}</p>
          <p className="log-location">{item.location}</p>
        </div>
        <p className="log-description">{item.description}</p>
        {item.tech && item.tech.length > 0 && (
          <div role="group" aria-label={`Technologies at ${item.company}`}>
            <dl className="stack">
              {groupTech(item.tech).map((row) => (
                <div className="stack-row" key={row.key}>
                  <dt>{row.label}</dt>
                  <dd>
                    <ul>
                      {row.tags.map((tag) => (
                        <li className="tag" key={tag}>
                          {tag}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </article>
    </li>
  );
};

const Experience = () => {
  return (
    <section id="experience" aria-labelledby="experience-title" className="section experience">
      <div className="container">
        <h2 id="experience-title" className="section-title">
          Experience
        </h2>
        <ol className="log">
          {experience.map((item) => (
            <LogRow key={item.id} item={item} />
          ))}
        </ol>
      </div>
    </section>
  );
};

export default Experience;
